import React, { useState, useEffect, useRef, use } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { supabase } from "../supabaseClient"; // Import supabase client
import { saveDraft, getDraft, deleteDraft } from "./common/DraftHistory";
import Cookies from "js-cookie";
import moment from "moment-timezone";
import { useTimezone } from "../Context/TimezoneContext";
import {
  storeNotificationChat,
  uploadAudioAndGetUrl,
  uploadFileAndGetUrl,
} from "../chatHistory";
import AddToSessionModal from "./Addtosessionmodal"; // Import at top
import Record from "./Record";
import UploadFile from "./UploadFile";
import ImageviewModal from "./common/ImageviewModal"; // Import common modal
import MentionsInput from "./common/MentionsInput"; // Import common MentionsInput
import ModeSelector, { getSelectedMode } from "./common/ModeSelector";
import { getContextLimitsForPayload } from "../utils/contextLimits";

// Helper function to get file icon based on file extension
const getFileIcon = (fileName, size = 24, color = "currentColor") => {
  const extension = fileName?.split(".").pop()?.toLowerCase() || "";

  const iconStyles = { width: size, height: size, flexShrink: 0 };

  // PDF Icon - Red
  if (extension === "pdf") {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M7 18H17V16H7V18ZM7 14H17V12H7V14ZM7 10H11V8H7V10ZM15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#E53935"/>
        <path d="M14 3V9H20L14 3Z" fill="#FFCDD2"/>
        <text x="12" y="19" fontSize="6" fontWeight="bold" fill="white" textAnchor="middle">PDF</text>
      </svg>
    );
  }

  // Excel/Spreadsheet Icon - Green
  if (["xlsx", "xls", "csv"].includes(extension)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#43A047"/>
        <path d="M14 3V9H20L14 3Z" fill="#C8E6C9"/>
        <path d="M7 13H9V15H7V13ZM10 13H12V15H10V13ZM13 13H15V15H13V13ZM7 16H9V18H7V16ZM10 16H12V18H10V16ZM13 16H15V18H13V16Z" fill="white"/>
      </svg>
    );
  }

  // Word Document Icon - Blue
  if (["docx", "doc"].includes(extension)) {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#1976D2"/>
        <path d="M14 3V9H20L14 3Z" fill="#BBDEFB"/>
        <path d="M7 13H17V14H7V13ZM7 15H17V16H7V15ZM7 17H13V18H7V17Z" fill="white"/>
      </svg>
    );
  }

  // Text File Icon - Gray
  if (extension === "txt") {
    return (
      <svg viewBox="0 0 24 24" fill="none" style={iconStyles}>
        <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2Z" fill="#757575"/>
        <path d="M14 3V9H20L14 3Z" fill="#E0E0E0"/>
        <path d="M7 13H17V14H7V13ZM7 15H17V16H7V15ZM7 17H12V18H7V17Z" fill="white"/>
      </svg>
    );
  }

  // Default Document Icon
  return (
    <svg viewBox="0 0 24 24" fill={color} style={iconStyles}>
      <path d="M15 2H5C3.9 2 3 2.9 3 4V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V8L15 2ZM19 20H5V4H14V9H19V20Z"/>
    </svg>
  );
};

// Helper function to get file type from file name
const getFileTypeFromName = (fileName) => {
  const extension = fileName?.split(".").pop()?.toLowerCase() || "";

  if (["jpg", "jpeg", "png", "gif", "bmp", "tiff", "webp"].includes(extension)) {
    return "image";
  }
  if (extension === "pdf") {
    return "pdf";
  }
  if (["xlsx", "xls"].includes(extension)) {
    return "excel";
  }
  if (extension === "csv") {
    return "csv";
  }
  if (["docx", "doc"].includes(extension)) {
    return "word";
  }
  if (extension === "txt") {
    return "txt";
  }
  return "document";
};

function ReplyModal({
  show,
  onHide,
  notification,
  handleSendReply,
  workflowUrl,
  onNotificationUpdate,
  alreadyReplied,
  onAlreadyRepliedChange,
  userId,
}) {
  // Get user timezone from context
  const { userTimezone } = useTimezone();

  const [audioRecording, setAudioRecording] = useState(false);
  const [cancelAudio, setCancelAudio] = useState(false);
  const [copyToTextArea, setCopyToTextArea] = useState(false);
  // Play/stop state for bot response audio (Deepgram logic)
  const [isPlayingId, setIsPlayingId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // For chunked audio queue
  const audioQueueRef = useRef([]);
  const currentAudioRef = useRef(null);
  // Deepgram-based play/stop logic for bot response audio (like ChatBot)
  const playResponseAudio = async (index, responseText, audioUrl, action) => {
    const MAX_CHARS = 1000;
    const deepgramUrl = "https://api.deepgram.com/v1/speak?model=aura-luna-en";
    const apiKey = process.env.REACT_APP_DEEPGRAM_API_KEY;

    // Stop logic
    if (action === "stop") {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      }
      if (audioQueueRef.current) {
        audioQueueRef.current = [];
      }
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      setIsPlaying(false);
      setIsPlayingId(null);
      return;
    }
    // Don't start if already playing
    if (isPlaying) return;
    setIsPlayingId(index);
    setIsPlaying(true);

    // If audioUrl (pre-generated audio) is available, just play it
    if (audioUrl) {
      const audio = new window.Audio(audioUrl);
      currentAudioRef.current = audio;
      audio.play();
      audio.onended = () => {
        setIsPlaying(false);
        setIsPlayingId(null);
        currentAudioRef.current = null;
      };
      return;
    }

    // Otherwise, use Deepgram API to generate and play audio in chunks
    const chunkText = (text, maxLength) => {
      if (text.length <= maxLength) return [text];
      const chunks = [];
      const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
      if (sentences.length === 0) return splitByWords(text, maxLength);
      let currentChunk = "";
      for (const sentence of sentences) {
        if (sentence.length > maxLength) {
          if (currentChunk) {
            chunks.push(currentChunk.trim());
            currentChunk = "";
          }
          const sentenceChunks = splitByWords(sentence, maxLength);
          chunks.push(...sentenceChunks);
          continue;
        }
        if ((currentChunk + sentence).length <= maxLength) {
          currentChunk += sentence;
        } else {
          chunks.push(currentChunk.trim());
          currentChunk = sentence;
        }
      }
      if (currentChunk) chunks.push(currentChunk.trim());
      return chunks;
    };
    const splitByWords = (text, maxLength) => {
      const chunks = [];
      const words = text.split(/\s+/);
      let currentChunk = "";
      for (const word of words) {
        if ((currentChunk + " " + word).length <= maxLength) {
          currentChunk += (currentChunk ? " " : "") + word;
        } else {
          if (currentChunk) chunks.push(currentChunk);
          if (word.length > maxLength) {
            for (let i = 0; i < word.length; i += maxLength) {
              chunks.push(word.substr(i, maxLength));
            }
            currentChunk = "";
          } else {
            currentChunk = word;
          }
        }
      }
      if (currentChunk) chunks.push(currentChunk);
      return chunks;
    };
    const processNextChunk = async () => {
      if (!audioQueueRef.current || audioQueueRef.current.length === 0) {
        setIsPlaying(false);
        setIsPlayingId(null);
        return;
      }
      const nextChunk = audioQueueRef.current.shift();
      try {
        const response = await fetch(deepgramUrl, {
          method: "POST",
          headers: {
            Authorization: `Token ${apiKey}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text: nextChunk }),
        });
        if (!response.ok)
          throw new Error(`Failed to fetch audio: ${response.statusText}`);
        const blob = await response.blob();
        const audioUrl = URL.createObjectURL(blob);
        const audio = new Audio(audioUrl);
        currentAudioRef.current = audio;
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          if (audioQueueRef.current.length > 0) {
            processNextChunk();
          } else {
            setIsPlaying(false);
            setIsPlayingId(null);
            currentAudioRef.current = null;
          }
        };
        await audio.play();
      } catch (error) {
        // Fallback to browser TTS if Deepgram fails
        if (responseText && window.speechSynthesis) {
          const utter = new window.SpeechSynthesisUtterance(responseText);
          utter.onend = () => {
            setIsPlaying(false);
            setIsPlayingId(null);
          };
          window.speechSynthesis.speak(utter);
        } else {
          setIsPlaying(false);
          setIsPlayingId(null);
        }
      }
    };
    // Start chunked playback
    audioQueueRef.current = chunkText(responseText, MAX_CHARS);
    await processNextChunk();
  };
  const [selectedNotificationRequest, setSelectedNotificationRequest] =
    useState([]);
  const [replyMessage, setReplyMessage] = useState("");
  const [selectedMode, setSelectedMode] = useState(() => getSelectedMode());
  // ...existing code...
  const [audioInput, setAudioInput] = useState(null); // Add this
  const [isUserQueyLoading, setIsUserQueyLoading] = useState(false); // Add this

  // Add file upload states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedFileUrl, setSelectedFileUrl] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("image");
  const [selectedFileName, setSelectedFileName] = useState("");

  // ...existing code...
  // Draft key for notification reply
  const draftKey =
    notification && userId ? `notification_${notification.id}_${userId}` : null;
  const [zoomeyApiUrl, setZoomeyAdkUrl] = useState(
    process.env.REACT_APP_ZOOMEY_API_URL
  );
  const [apiToken, setApiToken] = useState([]);
  const modalBodyRef = useRef(null);
  const textareaRef = useRef(null);
  const mentionsInputRef = useRef(null);

  const [showAddToSessionModal, setShowAddToSessionModal] = useState(false);
  const [addToChatText, setAddToChatText] = useState("");

  // Add these states
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isDarkModeInternal] = useState(false);
  const [isAudioProcessing, setIsAudioProcessing] = useState(false); // New state for audio processing

  // ...existing code...
  // Update useEffect to use sendChatMessage instead of handleSendMessage
  // useEffect(() => {
  //   if (audioInput) {
  //     handleAudioMessage();
  //   }
  // }, [audioInput]);

  function formatMentionSpacing(text) {
    return text.replace(/(@\w+):\s*/g, "$1: ");
  }

  useEffect(() => {
    if (!audioInput) return;
    (async () => {
      try {
        if (copyToTextArea) {
          setIsAudioProcessing(true); // Footer loader ON
          const transcript = await audioToText(audioInput);
          if (transcript) {
            setReplyMessage((prev) =>
              prev ? prev + " " + transcript : transcript
            );
            if (textareaRef.current) textareaRef.current.focus();
          }
          setIsAudioProcessing(false);// Footer loader OFF
          setAudioInput(null);
          setCopyToTextArea(false);
          return;
        }
        // Normal flow: process and send (handleAudioMessage reads audioInput)
        await handleAudioMessage();
      } catch (err) {
        setIsAudioProcessing(false);
        setAudioInput(null);
        setCopyToTextArea(false);
      }
    })();
  }, [audioInput, copyToTextArea]);

  useEffect(() => {
    if (!cancelAudio) return;
    setAudioInput(null);
    setIsAudioProcessing(false);
    setCopyToTextArea(false);
    setCancelAudio(false);
  }, [cancelAudio]);

  const handleAudioMessage = async () => {
    try {
      if (audioInput != null) {
        setIsLoading(true);
        setIsAudioProcessing(true); // Start audio processing indicator
        console.log("Processing audio input...");

        // Show temporary audio processing message in chat
        const tempProcessingId = `temp-${Date.now()}`;
        setSelectedNotificationRequest((prev) => {
          const currentList = prev || [];
          return [
            ...currentList,
            {
              id: tempProcessingId,
              user_query: "", // Empty query for now
              audioUrl: null,
              isProcessing: true, // Flag to show processing state
              createdAt: new Date().toISOString(),
              response: null,
            },
          ];
        });

        // Convert audio to text
        const audioText = await audioToText(audioInput);
        console.log("Audio transcribed:", audioText);

        // Combine input area text + transcript
        let finalText = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
        mentionsInputRef.current?.clearMention();
        if (audioText) {
          finalText = finalText
            ? `${finalText} ${audioText}` // Combine both
            : audioText; // Only transcript if input empty
        }

        if (audioText) {
          // Upload audio to Supabase storage
          let supabaseAudioUrl = null;
          try {
            supabaseAudioUrl = await uploadAudioAndGetUrl(
              notification?.id,
              userId,
              audioInput
            );
            console.log("Audio uploaded to:", supabaseAudioUrl);
          } catch (uploadError) {
            console.error("Audio upload failed:", uploadError);
            // Continue without audio URL if upload fails
          }

          // Store the notification chat with audio URL
          const dataGet = await storeNotificationChat(
            notification?.id,
            finalText, // <-- use combined text here
            supabaseAudioUrl, // Pass the audio URL (will be stored in 'path' column)
            userId // Pass user_id for chat history to work
          );
          console.log("Notification chat stored:", dataGet);

          let finalAudioText = audioText;
          if (
            selectedNotificationRequest &&
            selectedNotificationRequest.length <= 1
          ) {
            // Account for temp message
            finalAudioText =
              "For information this is my notification: " +
              notification?.message +
              "\n\n user_query: " +
              audioText;
          }

          // Remove temporary processing message and add real messages
          // setSelectedNotificationRequest((prev) => {
          //   const currentList = prev || [];
          //   // Remove temp processing message
          //   const withoutTemp = currentList.filter(
          //     (item) => item.id !== tempProcessingId
          //   );
          //   // Add BOTH audio message AND text message (separate bubbles like ChatBot)
          //   return [
          //     ...withoutTemp,
          //     // Audio message bubble (separate)
          //     {
          //       id: `${dataGet[0].id}_audio`,
          //       user_query: "", // Empty text for audio-only message
          //       audioUrl: supabaseAudioUrl,
          //       path: supabaseAudioUrl,
          //       createdAt: new Date().toISOString(),
          //       response: null,
          //       isAudioOnly: true, // Flag to identify audio-only message
          //     },
          //     // Text message bubble (separate)
          //     {
          //       id: dataGet[0].id,
          //       user_query: audioText,
          //       audioUrl: null, // No audio for text message
          //       path: null,
          //       createdAt: new Date().toISOString(),
          //       response: null,
          //       isTextOnly: true, // Flag to identify text-only message
          //     },
          //   ];
          // });

          setSelectedNotificationRequest((prev) => {
  const currentList = prev || [];
  // Remove temp processing message
  const withoutTemp = currentList.filter(
    (item) => item.id !== tempProcessingId
  );
  // Add BOTH audio message AND combined text+transcript bubble
  return [
    ...withoutTemp,
    // Audio message bubble (separate)
    {
      id: `${dataGet[0].id}_audio`,
      user_query: "", // Empty text for audio-only message
      audioUrl: supabaseAudioUrl,
      path: supabaseAudioUrl,
      createdAt: new Date().toISOString(),
      response: null,
      isAudioOnly: true, // Flag to identify audio-only message
    },
    // Combined text+transcript bubble (user query)
    {
      id: dataGet[0].id,
      user_query: finalText, // <-- Yeh combined input + transcript hai!
      audioUrl: null,
      path: null,
      createdAt: new Date().toISOString(),
      response: null,
      isTextOnly: true,
    },
  ];
});

          setIsAudioProcessing(false); // Stop audio processing indicator

          const payload = {
            user_id: userId,
            session_id: notification?.id.toString(),
            store_id: dataGet[0].id.toString(),
            user_query: finalText, // <-- use combined text here
            mode: selectedMode,
            customContextLimits: getContextLimitsForPayload(),
          };

          setReplyMessage("");

          // Delete draft after sending
          if (draftKey) await deleteDraft(draftKey);

          // Refresh token before API call
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) {
            setApiToken(session.access_token);
          }

          // Send to API
          const response = await fetch(zoomeyApiUrl + "/notification_chat", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify(payload),
          });

          if (response.status === 200) {
            console.log("API call successful");
            // Refresh the full conversation after response
            setTimeout(() => {
              getPopupContent(notification?.id);
            }, 1000); // Small delay to allow server processing
          } else {
            console.error("API call failed with status:", response.status);
            // alert("Something went wrong with your server.");
          }
        }
      }
    } catch (error) {
      console.error("Audio processing error:", error);
      // alert("Failed to process audio. Please try again.");
      // Remove temp processing message on error
      setSelectedNotificationRequest((prev) => {
        const currentList = prev || [];
        return currentList.filter((item) => !item.isProcessing);
      });
    } finally {
      setIsLoading(false);
      setIsAudioProcessing(false);
      setAudioInput(null);
    }
  };

  const audioToText = async (audioInput) => {
    try {
      // Get fresh session token
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("No valid session found. Please log in again.");
      }

      const formData = new FormData();
      formData.append("file", audioInput, "recording.mp3");

      const response = await fetch(zoomeyApiUrl + "/speech-to-text", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          // Don't include Content-Type - browser sets it automatically for FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `Upload failed: ${response.statusText} - ${
            errorData.error?.message || ""
          }`
        );
      }

      const data = await response.json();
      return data.text;
    } catch (error) {
      console.error("Error transcribing audio:", error);
      return null;
    }
  };

  // Add this function to handle audio recording stop
  const handleAudioRecorded = async (audioUrl) => {
    try {
      setIsLoading(true);
      // Optional: Do something with audioUrl
      console.log("Audio recorded:", audioUrl);
    } catch (error) {
      console.error("Error handling recorded audio:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // File upload functions
  const handleFileSelect = async (e) => {
    const file = e.target.files[0]; // Get only the first file
    if (file) {
      setSelectedFiles([file]); // Replace existing files with new single file

      // Create preview for the single file
      let preview;
      if (file.type.startsWith("image/")) {
        preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) =>
            resolve({
              file,
              type: "image",
              url: e.target.result,
              name: file.name,
            });
          reader.readAsDataURL(file);
        });
      } else {
        preview = {
          file,
          type: getFileTypeFromName(file.name),
          url: null,
          name: file.name,
        };
      }

      setFilePreviews([preview]); // Replace existing previews with new single preview
    }
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSendWithFiles = async () => {
    if (selectedFiles.length === 0 && replyMessage.trim() === "") return;

    try {
      setIsSending(true);

      // IMMEDIATELY clear the UI to give instant feedback to user
      const fileToSend = selectedFiles[0]; // Get the single file
      const messageToSend = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
      mentionsInputRef.current?.clearMention();

      // Clear UI immediately
      setSelectedFiles([]);
      setFilePreviews([]);
      setReplyMessage("");

      if (fileToSend) {
        // Process the single file
        const fileUrl = await uploadFileAndGetUrl(
          notification?.id,
          userId,
          fileToSend
        );

        if (!fileUrl) {
          console.error("File upload failed!");
          return;
        }

        // Only use the actual user message if present, otherwise just the fileUrl
        const userQueryToSend = messageToSend ? messageToSend : "";

        // Store file message in database (for UI)
        const dataGet = await storeNotificationChat(
          notification?.id,
          userQueryToSend,
          fileUrl,
          userId // Pass user_id for chat history to work
        );

        setSelectedNotificationRequest((prev) => [
          ...(prev || []),
          {
            id: dataGet[0].id,
            user_query: userQueryToSend,
            audioUrl: null,
            path: fileUrl,
            createdAt: new Date().toISOString(),
            response: null,
            isFileMessage: true,
            fileName: fileToSend.name,
            fileType: fileToSend.type,
            fileUrl: fileUrl,
            hasText: !!messageToSend,
          },
        ]);

        // Add Attachment_URL to user_query same as ChatBot format
        let userQueryWithAttachment = messageToSend;
        if (fileUrl) {
          userQueryWithAttachment = messageToSend
            ? messageToSend + `\n Attachment_URL: [${fileUrl}]`
            : `Attachment_URL: [${fileUrl}]`;
        }

        // Send to API (no file object needed, Attachment_URL is in user_query)
        const payload = {
          user_id: userId,
          session_id: notification?.id.toString(),
          store_id: dataGet[0].id.toString(),
          user_query: userQueryWithAttachment,
          mode: selectedMode,
          customContextLimits: getContextLimitsForPayload(),
        };
        // Refresh token before API call
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setApiToken(session.access_token);
        }
        
        const response = await fetch(zoomeyApiUrl + "/notification_chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(payload),
        });

        if (response.status === 200) {
          console.log("File message sent successfully");
        } else {
          console.error("File message API failed:", response.status);
        }
      } else if (messageToSend !== "") {
        // Send text message only
        await sendChatMessage(messageToSend);
      }

      // Delete draft after sending
      if (draftKey) await deleteDraft(draftKey);

      // Refresh conversation
      setTimeout(() => {
        getPopupContent(notification?.id);
      }, 1000);
    } catch (error) {
      console.error("Error sending files:", error);
      // alert("Failed to upload files. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const openFileModal = (fileUrl, fileType = "image", fileName = "File") => {
    setSelectedFileUrl(fileUrl);
    setSelectedFileType(fileType);
    setSelectedFileName(fileName);
    setShowFileModal(true);
  };

  // ...existing code...

  const formatResponseMessage = (text) => {
    if (!text) return "";
    // If text is already HTML (from backend formatToHTML), linkify any remaining plain URLs
    if (/<[a-z][\s\S]*>/i.test(text)) {
      return text.replace(/((?:^|[^"'=/>]))(https?:\/\/[^\s<>\[\]"']+)/g,
        (match, prefix, url) => `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
      );
    }
    let html = text;
    html = html.replace(/\\n/g, "<br />").replace(/\n/g, "<br />");

    // Protect URLs from double-processing
    const urlPlaceholders = [];
    html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (match, linkText, url) => {
      urlPlaceholders.push({ type: "markdown", text: linkText, url });
      return `URLPLACEHOLDER${urlPlaceholders.length - 1}ENDURL`;
    });
    html = html.replace(/(https?:\/\/[^\s<>\[\]()]+)/g, (match) => {
      urlPlaceholders.push({ type: "plain", url: match });
      return `URLPLACEHOLDER${urlPlaceholders.length - 1}ENDURL`;
    });

    html = html
      .replace(/###\s*(.*?)(?:<br \/>|$)/g, "<h5>$1</h5>")
      .replace(/##\s*(.*?)(?:<br \/>|$)/g, "<h4>$1</h4>")
      .replace(/#\s*(.*?)(?:<br \/>|$)/g, "<h3>$1</h3>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/(\d+)\.\s+(.*?)(?:<br \/>|$)/g, '<ol start="$1"><li>$2</li></ol>')
      .replace(
        /`(.*?)`/g,
        '<code style="background-color: #f0f0f0; padding: 2px 4px; border-radius: 4px; font-family: monospace;">$1</code>'
      );

    // Restore URLs as proper <a> tags
    html = html.replace(/URLPLACEHOLDER(\d+)ENDURL/g, (match, index) => {
      const entry = urlPlaceholders[parseInt(index)];
      if (entry.type === "markdown") {
        return `<a href="${entry.url}" target="_blank" rel="noopener noreferrer">${entry.text}</a>`;
      }
      return `<a href="${entry.url}" target="_blank" rel="noopener noreferrer">${entry.url}</a>`;
    });

    html = html.replace(/\{(.*?)\}/g, "$1");
    return html;
  };

  const getTimeZone = (created_at) => {
    return moment
      .utc(created_at)
      .tz(userTimezone)
      .format("MM-DD-YYYY hh:mm A");
  };

  const storeApiToken = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) {
      console.warn("No session found. User may not be logged in.");
      return;
    }
    setApiToken(session.access_token);
  };

  const getPopupContent = async (requestId) => {
    try {
      const { data, error } = await supabase
        .from("notification_chat")
        .select("*, path") // Use 'path' column instead of 'audio_url'
        .eq("notification_id", requestId)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching notification chat:", error);
        return;
      }

      // Map the data to include audioUrl for consistency with component state
      const mappedData =
        data?.map((item) => {
          const hasAudio =
            item.path &&
            item.path.includes("/audios/") &&
            item.path.trim() !== "";
          const hasFile =
            item.path &&
            !item.path.includes("/audios/") &&
            item.path.trim() !== "";
          const hasText = item.user_query && item.user_query.trim() !== "";

          // Check if it's a file message (with or without text)
          if (hasFile && hasText) {
            // Check if user_query contains file reference
            const isFileReference =
              item.user_query.includes("Shared image:") ||
              item.user_query.includes("Shared file:") ||
              item.user_query.includes(".jpg") ||
              item.user_query.includes(".png") ||
              item.user_query.includes(".pdf") ||
              item.user_query.includes(".doc");

            if (isFileReference) {
              // Pure file message
              return {
                ...item,
                audioUrl: null,
                fileUrl: item.path,
                isFileMessage: true,
                fileName: item.user_query
                  .replace("Shared image: ", "")
                  .replace("Shared file: ", ""),
                fileType: item.user_query.includes("Shared image:")
                  ? "image/"
                  : "application/",
              };
            } else {
              // File with text message - show both
              const detectedFileType =
                item.path.includes("images/") ||
                item.path.includes(".jpg") ||
                item.path.includes(".jpeg") ||
                item.path.includes(".png") ||
                item.path.includes(".gif") ||
                item.path.includes(".webp")
                  ? "image/"
                  : "application/";

              return {
                ...item,
                audioUrl: null,
                fileUrl: item.path,
                isFileMessage: true,
                fileName: item.path.split("/").pop() || "file",
                fileType: detectedFileType,
                hasText: true, // Flag to show text along with file
              };
            }
          }
          // If only file (no text), but has file path
          else if (hasFile && !hasText) {
            const detectedFileType =
              item.path.includes("images/") ||
              item.path.includes(".jpg") ||
              item.path.includes(".jpeg") ||
              item.path.includes(".png") ||
              item.path.includes(".gif") ||
              item.path.includes(".webp")
                ? "image/"
                : "application/";

            return {
              ...item,
              audioUrl: null,
              fileUrl: item.path,
              isFileMessage: true,
              fileName: item.path.split("/").pop() || "file",
              fileType: detectedFileType,
              hasText: false,
            };
          }
          // If message has both audio and text, we need to create separate entries
          else if (hasAudio && hasText) {
            return [
              // Audio-only message
              {
                ...item,
                id: `${item.id}_audio`,
                audioUrl: item.path,
                user_query: "", // Empty text for audio-only
                isAudioOnly: true,
              },
              // Text-only message
              {
                ...item,
                id: item.id,
                audioUrl: null,
                path: null,
                isTextOnly: true,
              },
            ];
          } else if (hasAudio && !hasText) {
            // Audio-only message
            return {
              ...item,
              audioUrl: item.path,
              isAudioOnly: true,
            };
          } else {
            // Text-only message
            return {
              ...item,
              audioUrl: item.path, // Keep for backward compatibility
            };
          }
        }) || [];

      // Flatten the array in case we have nested arrays from audio+text messages
      const flattenedData = mappedData.flat();

      setSelectedNotificationRequest(flattenedData);
      console.log("ReplyModal - Fetched notification data:", flattenedData);
      // Debug file messages specifically
      flattenedData.forEach((item, index) => {
        if (item.isFileMessage) {
          console.log(`ReplyModal - File message ${index}:`, {
            id: item.id,
            fileUrl: item.fileUrl,
            fileName: item.fileName,
            fileType: item.fileType,
            path: item.path,
            hasText: item.hasText,
            user_query: item.user_query,
          });
        }
      });
    } catch (error) {
      console.error("Error in getPopupContent:", error);
    }
  };

  useEffect(() => {
    if (notification && notification.id) {
      getPopupContent(notification.id);
      storeApiToken();
      // Fetch draft for this notification
      if (draftKey) {
        getDraft(draftKey).then((val) => {
          if (val !== null && val !== undefined) {
            setReplyMessage(val);
          } else {
            setReplyMessage(""); // <-- Yeh line add karo
          }
        });
      } else {
        setReplyMessage(""); // <-- Yeh bhi add karo, agar draftKey hi nahi bana
      }
    } else {
      setReplyMessage(""); // <-- Yeh bhi add karo, agar notification hi nahi hai
    }
    // eslint-disable-next-line
  }, [notification, userId]);

  useEffect(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = modalBodyRef.current.scrollHeight;
    }
  }, [selectedNotificationRequest]);

  const sendChatMessage = async (message) => {
    try {
      setReplyMessage("");
      // console.log("Sending message:", message);
      const formattedMessage = formatMentionSpacing(message); // Format @option: spacing
      if (formattedMessage.trim() !== "") {
        const dataGet = await storeNotificationChat(
          notification?.id,
          formattedMessage,
          null,
          userId // Pass user_id for chat history to work
        );

        let finalMessage = formattedMessage;
        if (
          selectedNotificationRequest &&
          selectedNotificationRequest.length == 0
        ) {
          finalMessage =
            "For information this is my notification: " +
            notification?.message +
            "\n\n user_query: " +
            message;
        }

        const payload = {
          user_id: userId,
          session_id: notification?.id.toString(),
          store_id: dataGet[0].id.toString(),
          user_query: finalMessage,
          mode: selectedMode,
          customContextLimits: getContextLimitsForPayload(),
        };

        // Update local state immediately
        setSelectedNotificationRequest((prev) => [
          ...(prev || []),
          {
            id: dataGet[0].id,
            user_query: message,
            audioUrl: null,
            path: null,
            createdAt: new Date().toISOString(),
            response: null,
          },
        ]);

        setReplyMessage("");
        // Delete draft after sending
        if (draftKey) await deleteDraft(draftKey);

        // Refresh token before API call
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setApiToken(session.access_token);
        }

        const response = await fetch(zoomeyApiUrl + "/notification_chat", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(payload),
        });

        if (response.status === 200) {
          console.log("Text message sent successfully");
          // Refresh the full conversation after response
          setTimeout(() => {
            getPopupContent(notification?.id);
          }, 1000);
        } else {
          console.error(
            "Text message API failed with status:",
            response.status
          );
          // alert("Something went wrong with your server.");
        }
      }
    } catch (error) {
      console.error("Error sending text message:", error);
      // alert("Failed to send message. Please try again.");
    }
  };

  // return(<> </>)
  return (
    <>
      <style>
  {`
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    .audio-processing-spinner {
      animation: spin 1s linear infinite;
    }
  `}
</style> 
      <Modal show={show} onHide={onHide} size="xl" centered className="max_width_modal">
        <Modal.Header closeButton>
          <Modal.Title>Notification Chat</Modal.Title>
        </Modal.Header>
        <Modal.Body
          ref={modalBodyRef}
          className="custom-scrollbar"
          style={{
            maxHeight: "70vh",
            overflowY: "auto",
            backgroundColor: "var(--chatbot-bg)",
          }}
        >
          <div
            style={{ display: "flex", flexDirection: "column", width: "100%" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                marginBottom: "12px",
                width: "100%",
                position: "relative", // for dropdown
              }}
            >
              <div
                className="w-100"
                style={{
                  backgroundColor: "transparent",
                  color: "var(--text)",
                  padding: "12px 16px 12px 45px",
                  borderRadius: "12px",
                  width: "100%",
                  wordBreak: "break-word",
                }}
              >
                {/* 3-dot dropdown for main notification message */}
                <div style={{ position: "absolute", top: 8, right: 8 }}>
                  <div className="dropdown">
                    <i
                      className="bi bi-three-dots-vertical"
                      style={{ cursor: "pointer", fontSize: "1.2em" }}
                      id={`dropdownMenuButton-main`}
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    ></i>
                    <ul
                      className="dropdown-menu cursor-pointer"
                      aria-labelledby={`dropdownMenuButton-main`}
                    >
                      <li
                        className="dropdown-item cursor-pointer"
                        onClick={() => {
                          setShowAddToSessionModal(true);
                          setAddToChatText(notification?.message || "");
                        }}
                      >
                        <i className="bi bi-plus-square-dotted"></i>
                        &nbsp;Add to Topic
                      </li>
                    </ul>
                  </div>
                </div>
                <span
                  className="zoomey-title"
                  style={{ fontWeight: "bold", marginLeft: "-47px" }}
                >
                  <span className="chatbot_z_logo">Z</span> Zoomey
                </span>
                <div
                  style={{ paddingRight: "40px" }}
                  dangerouslySetInnerHTML={{
                    __html: formatResponseMessage(
                      notification?.message || "Thinking..."
                    ),
                  }}
                />
                <span>
                  {getTimeZone(notification?.created_at)}
                </span>
              </div>
            </div>
            {selectedNotificationRequest &&
            selectedNotificationRequest.length > 0 ? (
              selectedNotificationRequest.map((request, index) => (
                <React.Fragment key={request.id || index}>
                  {/* User message (right) */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: "12px",
                      width: "100%",
                    }}
                  >
                    <div
                      className="chat-user-msg"
                      style={{
                        background: request.isAudioOnly
                          ? "transparent"
                          : "var(--teal-bg)",
                        color: "var(--textwhite)",
                        padding: request.isAudioOnly ? "0" : "12px 16px", // No padding for audio-only like ChatBot
                        borderRadius: "12px",
                        maxWidth: "70%",
                        wordBreak: "break-word",
                        position: "relative",
                      }}
                    >
                      <div
                        style={
                          {
                            // paddingRight: request.isAudioOnly ? "0" : "40px",
                          }
                        }
                      >
                        {/* Show processing state for audio */}
                        {request.isProcessing ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "8px 12px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <i
                                className="bi bi-mic-fill"
                                style={{ fontSize: "14px", opacity: 0.7 }}
                              ></i>
                              <span style={{ fontSize: "12px", opacity: 0.7 }}>
                                Processing voice message
                              </span>
                            </div>
                            <div
                              className="audio-processing-spinner"
                              style={{
                                width: "16px",
                                height: "16px",
                                border: "2px solid rgba(255,255,255,0.3)",
                                borderTop: "2px solid white",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            ></div>
                          </div>
                        ) : request.isAudioOnly ? (
                          /* Audio-only bubble - exactly like ChatBot */
                          <audio
                            controls
                            src={request.audioUrl}
                            style={{
                              maxWidth: "100%",
                              width: "250px", // Fixed width like ChatBot
                            }}
                          />
                        ) : request.isFileMessage ? (
                          /* File message display */
                          <div>
                            {request.fileType &&
                            request.fileType.startsWith("image/") ? (
                              /* Image message - show larger image like in your example */
                              <div>
                                <img
                                  src={request.fileUrl}
                                  alt={request.fileName}
                                  style={{
                                    maxWidth: "200px",
                                    maxHeight: "200px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    marginBottom: request.hasText ? "8px" : "0",
                                  }}
                                  onClick={() =>
                                    openFileModal(
                                      request.fileUrl,
                                      "image",
                                      request.fileName
                                    )
                                  }
                                  onError={(e) => {
                                    // Fallback to smaller thumbnail if main image fails
                                    e.target.style.maxWidth = "60px";
                                    e.target.style.maxHeight = "60px";
                                  }}
                                />
                                {/* Show text below image if exists */}
                                {request.hasText &&
                                  request.user_query &&
                                  !request.user_query.includes(
                                    "Shared image:"
                                  ) &&
                                  !request.user_query.includes(
                                    "Shared file:"
                                  ) && (
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        marginTop: "4px",
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: formatResponseMessage(request.user_query),
                                      }}
                                    />
                                  )}
                              </div>
                            ) : (
                              /* Non-image file message - show with icon */
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "8px 12px",
                                    background: "rgba(255,255,255,0.1)",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    marginBottom: request.hasText ? "8px" : "0",
                                  }}
                                  onClick={() => {
                                    // Check if it's a PDF or other file type
                                    if (
                                      request.fileUrl.includes(".pdf") ||
                                      request.fileName
                                        .toLowerCase()
                                        .includes(".pdf")
                                    ) {
                                      openFileModal(
                                        request.fileUrl,
                                        "pdf",
                                        request.fileName
                                      );
                                    } else {
                                      // For other file types, try to open in modal or fallback to new tab
                                      openFileModal(
                                        request.fileUrl,
                                        "other",
                                        request.fileName
                                      );
                                    }
                                  }}
                                >
                                  {getFileIcon(request.fileName, 24)}
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {request.fileName}
                                    </div>
                                  </div>
                                </div>
                                {/* Show text below file if exists */}
                                {request.hasText &&
                                  request.user_query &&
                                  !request.user_query.includes(
                                    "Shared image:"
                                  ) &&
                                  !request.user_query.includes(
                                    "Shared file:"
                                  ) && (
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        marginTop: "4px",
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: formatResponseMessage(request.user_query),
                                      }}
                                    />
                                  )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: "flex", alignItems: "start" }}>
                            <span
                              style={{ flex: 1, paddingRight: "12px" }}
                              dangerouslySetInnerHTML={{
                                __html: formatResponseMessage(
                                  formatMentionSpacing(
                                    request.user_query ||
                                      "User message text goes here."
                                  )
                                ),
                              }}
                            />
                            {/* Only show edit/copy for user query, no play/copy/3-dot here! */}
                            <span
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginLeft: "8px",
                                gap: "8px",
                              }}
                            >
                              {/* Copy icon */}
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="currentColor"
                                onClick={() => {
                                  let textToCopy = request.user_query;
                                  const selectedText = window
                                    .getSelection()
                                    .toString();
                                  if (selectedText) {
                                    textToCopy = selectedText;
                                  }
                                  navigator.clipboard.writeText(textToCopy);
                                }}
                                style={{ cursor: "pointer" }}
                                title="Copy message"
                              >
                                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                              </svg>
                              {/* Edit icon */}
                              <i
                                className="bi bi-pencil-fill"
                                style={{ fontSize: "12px", cursor: "pointer" }}
                                title="Edit message"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReplyMessage(request.user_query);
                                  if (textareaRef.current) {
                                    textareaRef.current.focus();
                                  }
                                }}
                              ></i>
                            </span>
                          </div>
                        )}
                      </div>
                      {/* {!request.isProcessing && !request.isAudioOnly && (
                        <div style={{ fontSize: "12px", opacity: 0.7, marginTop: "4px" }}>
                          {getTimeZone(request.createdAt) || "12:01 PM"}
                        </div>
                      )} */}
                    </div>
                  </div>

                  {/* Bot message (left) - Only show for text messages, not audio-only */}
                  {!request.isAudioOnly && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        marginBottom: "12px",
                        width: "100%",
                        position: "relative", // for dropdown
                      }}
                    >
                      <div
                        className="w-100"
                        style={{
                          background: request.isAudioOnly
                            ? "transparent"
                            : "transparent",
                          color: "var(--text)",
                          padding: "12px 16px 12px 45px",
                          borderRadius: "12px",
                          Width: "100%",
                          wordBreak: "break-word",
                          position: "relative",
                        }}
                      >
                        {/* Play, Copy, then 3-dot menu for response */}
                        <div
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          {/* Play/Stop icon logic like ChatBot.jsx */}
                          {isPlayingId === index && isPlaying ? (
                            <i
                              className="bi bi-stop-fill"
                              style={{ fontSize: "18px", cursor: "pointer" }}
                              title="Stop response audio"
                              onClick={async () =>
                                playResponseAudio(
                                  index,
                                  request.response,
                                  request.audioUrl,
                                  "stop"
                                )
                              }
                            ></i>
                          ) : (
                            <i
                              className="bi bi-play-fill"
                              style={{
                                fontSize: "18px",
                                cursor:
                                  isPlaying && isPlayingId !== index
                                    ? "not-allowed"
                                    : "pointer",
                                opacity:
                                  isPlaying && isPlayingId !== index ? 0.5 : 1,
                              }}
                              title="Play response audio"
                              onClick={async () => {
                                if (!isPlaying || isPlayingId === index) {
                                  playResponseAudio(
                                    index,
                                    request.response,
                                    request.audioUrl,
                                    "play"
                                  );
                                }
                              }}
                              disabled={isPlaying && isPlayingId !== index}
                            ></i>
                          )}
                          {/* Copy icon for response */}
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            onClick={() => {
                              let textToCopy = request.response || "";
                              // Remove HTML tags for plain text copy (like ChatBot)
                              const tempDiv = document.createElement("div");
                              tempDiv.innerHTML = textToCopy;
                              let plainText =
                                tempDiv.textContent || tempDiv.innerText || "";
                              // If user selected text, use that (also strip tags)
                              const selectedText = window
                                .getSelection()
                                .toString();
                              if (selectedText) {
                                const tempSel = document.createElement("div");
                                tempSel.innerHTML = selectedText;
                                plainText =
                                  tempSel.textContent ||
                                  tempSel.innerText ||
                                  "";
                              }
                              navigator.clipboard.writeText(plainText);
                            }}
                            style={{ cursor: "pointer" }}
                            title="Copy response"
                          >
                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                          </svg>
                          {/* 3-dot dropdown for each bot response */}
                          <div
                            className="dropdown"
                            style={{ display: "inline-block" }}
                          >
                            <i
                              className="bi bi-three-dots-vertical"
                              style={{ cursor: "pointer", fontSize: "1.2em" }}
                              id={`dropdownMenuButton-bot-${index}`}
                              data-bs-toggle="dropdown"
                              aria-expanded="false"
                            ></i>
                            <ul
                              className="dropdown-menu"
                              aria-labelledby={`dropdownMenuButton-bot-${index}`}
                            >
                              <li
                                className="dropdown-item"
                                onClick={() => {
                                  setShowAddToSessionModal(true);
                                  setAddToChatText(request.response || "");
                                }}
                              >
                                <i className="bi bi-plus-square-dotted"></i> Add
                                to Topic
                              </li>
                            </ul>
                          </div>
                        </div>
                        <span
                          className="zoomey-title"
                          style={{ fontWeight: "bold", marginLeft: "-47px" }}
                        >
                          <span className="chatbot_z_logo">Z</span>
                          Zoomey
                        </span>
                        <div
                          style={{ paddingRight: "40px" }}
                          dangerouslySetInnerHTML={{
                            __html: formatResponseMessage(
                              request?.response ||
                                (() => {
                                  // Check if this is the last message
                                  const isLastMessage = index === selectedNotificationRequest.length - 1;
                                  // Check if request is older than 1 minute
                                  const requestTime = moment.utc(request.createdAt);
                                  const now = moment();
                                  const isOlderThanOneMinute = now.diff(requestTime, 'minutes') >= 1;

                                  if (isLastMessage && !isOlderThanOneMinute) {
                                    return "Thinking...";
                                  }
                                  return "You stopped this request";
                                })()
                            ),
                          }}
                        />
                        {/* <br /> */}
                        <span>{getTimeZone(request.created_at)}</span>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))
            ) : (
              <> </>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              gap: "8px",
            }}
          >
            {/* File preview section */}
            {filePreviews.length > 0 && (
              <div
                style={{
                  maxHeight: "120px",
                  overflowY: "auto",
                  padding: "8px",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  background: "#f8f9fa",
                }}
              >
                {filePreviews.map((preview, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "8px 12px",
                      background: "#fff",
                      borderRadius: "8px",
                      margin: "4px 0",
                      border: "1px solid #dee2e6",
                    }}
                  >
                    {preview.type === "image" ? (
                      <>
                        <img
                          src={preview.url}
                          alt={preview.name}
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "2px solid #e0e0e0",
                          }}
                        />
                        <span
                          style={{
                            flex: 1,
                            marginLeft: "12px",
                            fontSize: "14px",
                            color: "#495057",
                          }}
                        >
                          {preview.name}
                        </span>
                      </>
                    ) : (
                      <>
                        {getFileIcon(preview.name, 28)}
                        <span
                          style={{
                            flex: 1,
                            marginLeft: "12px",
                            fontSize: "14px",
                            color: "#495057",
                          }}
                        >
                          {preview.name}
                        </span>
                      </>
                    )}
                    <button
                      onClick={() => removeFile(index)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#dc3545",
                        cursor: "pointer",
                        fontSize: "18px",
                        padding: "4px 8px",
                        borderRadius: "4px",
                      }}
                      title="Remove file"
                      onMouseEnter={(e) =>
                        (e.target.style.background = "#f8f9fa")
                      }
                      onMouseLeave={(e) => (e.target.style.background = "none")}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input section */}
            {isAudioProcessing && copyToTextArea && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  marginBottom: "8px",
                }}
                className="w-100"
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTop: "3px solid var(--teal-bg)",
                      borderRadius: "50%",
                      animation: "spin .5s linear infinite",
                    }}
                  ></div>
                  <i
                    className="bi bi-mic-fill"
                    style={{
                      fontSize: "14px",
                      opacity: 0.7,
                      color: "var(--text)",
                    }}
                  ></i>
                  <span
                    style={{
                      fontSize: "12px",
                      opacity: 0.7,
                      color: "var(--text)",
                    }}
                  >
                    Processing your voice...
                  </span>
                </div>
              </div>
            )}
            <div
              style={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <ModeSelector onModeChange={setSelectedMode} />
              <MentionsInput
                ref={mentionsInputRef}
                inputText={replyMessage}
                setInputText={async (text) => {
                  setReplyMessage(text);
                  // Save draft on every change
                  if (draftKey) await saveDraft(draftKey, text);
                }}
                textareaRef={textareaRef}
                onPasteFile={(file, preview) => {
                  setSelectedFiles([file]);
                  setFilePreviews([preview]);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    if (filePreviews.length > 0) {
                      handleSendWithFiles();
                    } else if (replyMessage.trim() !== "" || mentionsInputRef.current?.getEffectiveQuery()) {
                      const q = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
                      mentionsInputRef.current?.clearMention();
                      sendChatMessage(q);
                    }
                  }
                }}
                placeholder="What should I help draft or resolve here?"
                // className="form-control"
                style={{ resize: "none", flex: 1 }}
                rows={4}
              />
              {!audioRecording && (
                <>
                  <button
                    onClick={() => {
                      if (filePreviews.length > 0) {
                        handleSendWithFiles();
                      } else {
                        const q = mentionsInputRef.current?.getEffectiveQuery() || replyMessage.trim();
                        mentionsInputRef.current?.clearMention();
                        sendChatMessage(q);
                      }
                    }}
                    disabled={
                      (replyMessage.trim() === "" &&
                        filePreviews.length === 0) ||
                      isSending
                    }
                    style={{
                      border: "none",
                      background: "var(--teal-bg)",
                      padding: "8px",
                      borderRadius: "8px",
                      width: "40px",
                      height: "40px",
                    }}
                  >
                    <i className="bi bi-send"></i>
                  </button>

                  {/* File upload button */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                    accept="image/*,.pdf,.xlsx,.xls,.csv,.docx,.doc,.txt"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    // style={{ border: "none", background: "none", padding: "8px" }}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      border: "1px solid var(--border-color)",
                      background: "transparent",
                      color: "var(--text)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      opacity: isLoading || isSending || isStreaming ? 0.5 : 1,
                    }}
                    title="Attach file"
                    disabled={isLoading || isSending || isStreaming}
                  >
                    <i
                      className="bi bi-paperclip"
                      style={{ fontSize: "20px", cursor: "pointer" }}
                    ></i>
                  </button>
                </>
              )}
              <Record
                onStop={handleAudioRecorded}
                setAudioInput={setAudioInput}
                setAudioRecording={setAudioRecording}
                isLoading={isLoading || isSending || isStreaming}
                setIsUserQueyLoading={setIsUserQueyLoading}
                isDarkModeInternal={isDarkModeInternal}
                // setIsAudioProcessing={setIsAudioProcessing}
                setIsAudioProcessing={setIsAudioProcessing}
                setIsVoiceProcessing={setIsAudioProcessing} // <-- ADD THIS LINE
                setCopyToTextArea={setCopyToTextArea}
                setCancelAudio={setCancelAudio}
              />
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <AddToSessionModal
        show={showAddToSessionModal}
        handleClose={() => setShowAddToSessionModal(false)}
        addToChatText={addToChatText}
        userId={userId}
      />

      {/* Common File Viewer Modal - Handles both Images and PDFs */}
      <ImageviewModal
        show={showFileModal}
        onHide={() => setShowFileModal(false)}
        fileUrl={selectedFileUrl}
        fileType={selectedFileType}
        fileName={selectedFileName}
        altText="Chat File Preview"
      />

      <UploadFile userId={userId} />
    </>
  );
}

export default ReplyModal;
